import type { Dispatch, UnknownAction } from 'redux';
import { setAuthenticated, setToken, setUser } from './auth-slice.ts';

export interface GuardConfig {
  idp_uri: string;
  get_user: () => Promise<any>;
  redirect_uri: string;
}

interface GuardSession {
  code: string | null;
  tenant: string | null;
  returnedState?: string | null;
  refreshToken: string | null;
  accessToken: string | null;
}

const userCode = new WeakMap<IDPGuard, GuardSession>();

class TokenRefreshUnavailableError extends Error {
  constructor() {
    super('The sign-in service is temporarily unavailable. Please try again.');
    this.name = 'TokenRefreshUnavailableError';
  }
}

export default class IDPGuard {
  private static initializationPromise: Promise<boolean> | null = null;
  private static logoutPromise: Promise<void> | null = null;
  private static refreshPromise: Promise<void> | null = null;
  private static config: GuardConfig;
  private static authToken: string | null = null;
  private static tenantName: string | null = null;
  private static clientId: string | null = null;
  private static tokenParsed: any | null = null;
  private static userInfo: any | null = null;
  private static dispatch: Dispatch<UnknownAction>;
  private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(config: GuardConfig, dispatch: Dispatch<UnknownAction>) {
    IDPGuard.checkInstance(this);
    IDPGuard.config = config;
    IDPGuard.dispatch = dispatch;
    userCode.set(this, {
      code: null,
      tenant: null,
      refreshToken: null,
      accessToken: null,
    });
  }

  private static checkInstance(instance: unknown): void {
    if (!(instance instanceof IDPGuard)) {
      throw new Error("The constructor must be invoked with the keyword 'new'");
    }
  }

  private static getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  }

  private static setCookie(name: string, value: string, days = 7): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const expiresString = `expires=${expires.toUTCString()}`;
    document.cookie = `${name}=${value}; ${expiresString}; path=/`;
  }

  private static removeCookie(name: string, path = '/', domain = ''): void {
    const expires = new Date();
    expires.setTime(expires.getTime() - 1000);
    const expiresString = `expires=${expires.toUTCString()}`;
    let cookieString = `${name}=; ${expiresString}; path=${path};`;

    if (domain) {
      cookieString += ` domain=${domain};`;
    }

    document.cookie = cookieString;
  }

  private static parseToken(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');
    if (base64) {
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((character) =>
            `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`,
          )
          .join(''),
      );
      return JSON.parse(jsonPayload);
    }
  }

  async init(): Promise<boolean> {
    if (!IDPGuard.initializationPromise) {
      const attempt = this.initialize();
      const guardedAttempt = attempt.catch((error) => {
        // Allow a later mount to retry if initialization failed before auth
        // could settle (for example, a temporary network failure).
        if (IDPGuard.initializationPromise === guardedAttempt) {
          IDPGuard.initializationPromise = null;
        }
        throw error;
      });
      IDPGuard.initializationPromise = guardedAttempt;
    }
    return IDPGuard.initializationPromise;
  }

  private async initialize(): Promise<boolean> {
    const url = new URL(window.location.href);
    const queryParams = url.searchParams;
    const tenant = queryParams.get('tenant');
    const code = queryParams.get('code');
    const returnedState = queryParams.get('state');

    queryParams.delete('code');
    queryParams.delete('tenant');
    queryParams.delete('state');
    window.history.replaceState({}, '', url.pathname + url.search);

    IDPGuard.authToken = IDPGuard.getCookie('ACCESS_TOKEN');
    IDPGuard.tenantName = tenant || IDPGuard.getCookie('TENANT_NAME');
    IDPGuard.clientId = 'web-client';

    const userContext = userCode.get(this)!;
    userContext.tenant = tenant;
    userContext.code = code;
    userContext.returnedState = returnedState;

    if (code) {
      await this.tokenExchange();
    } else if (IDPGuard.authToken) {
      await this.refreshToken();
    } else {
      await this.login();
    }

    if (IDPGuard.authToken) {
      IDPGuard.tokenParsed = IDPGuard.parseToken(IDPGuard.authToken);
      await this.userData();
      this.setupTokenRefreshInterval();
    }

    return !!IDPGuard.authToken;
  }

  setupTokenRefreshInterval(): void {
    if (this.refreshTimeoutId !== null) clearTimeout(this.refreshTimeoutId);
    if (IDPGuard.authToken && IDPGuard.tokenParsed) {
      const currentTime = Math.floor(Date.now() / 1000);
      const expiresIn = IDPGuard.tokenParsed.exp - currentTime;
      const refreshTimeout = Math.max(0, expiresIn - 60) * 1000;

      this.scheduleTokenRefresh(refreshTimeout);
    }
  }

  private scheduleTokenRefresh(delayMs: number): void {
    if (this.refreshTimeoutId !== null) clearTimeout(this.refreshTimeoutId);
    this.refreshTimeoutId = setTimeout(() => {
      this.refreshTimeoutId = null;
      void this.refreshToken().catch((error: unknown) => {
        if (error instanceof TokenRefreshUnavailableError && IDPGuard.authToken && !IDPGuard.logoutPromise) {
          // Keep the refresh credential for recovery; the server still rejects
          // expired access tokens while the sign-in service is unavailable.
          this.scheduleTokenRefresh(30_000);
        }
        console.warn('[auth] Background token refresh failed', error instanceof Error ? error.message : 'Unknown error');
      });
    }, delayMs);
  }

  private async prepareLoginUrl(
    options: { stashReturnTo?: boolean } = {},
  ): Promise<string> {
    const { stashReturnTo = true } = options;
    if (stashReturnTo) {
      try {
        const here =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        if (here && here !== '/' && !here.startsWith('/?code=')) {
          window.sessionStorage.setItem('art.console.return_to', here);
        }
      } catch {
        // sessionStorage unavailable — accept the default landing.
      }
    }

    const params = new URLSearchParams({
      'redirect-uri': IDPGuard.config.redirect_uri,
    });

    if (!isPkceDisabled()) {
      const challenge = await createPkcePair();
      const state = randomBase64Url(32);
      try {
        window.sessionStorage.setItem(
          pkceStorageKey(state),
          JSON.stringify({ verifier: challenge.verifier, createdAt: Date.now() }),
        );
      } catch {
        // Storage-disabled browsers retain the legacy non-PKCE fallback.
      }
      params.set('code_challenge', challenge.challenge);
      params.set('code_challenge_method', 'S256');
      params.set('state', state);
    }

    return `${IDPGuard.config.idp_uri}/idp/auth/tenant/login?${params.toString()}`;
  }

  async login(): Promise<void> {
    const loginUrl = await this.prepareLoginUrl();
    window.location.href = loginUrl;
  }

  async logout(): Promise<void> {
    if (!IDPGuard.logoutPromise) {
      IDPGuard.logoutPromise = this.performLogout();
    }
    return IDPGuard.logoutPromise;
  }

  private async performLogout(): Promise<void> {
    if (this.refreshTimeoutId !== null) clearTimeout(this.refreshTimeoutId);
    this.refreshTimeoutId = null;
    const token = IDPGuard.authToken;
    const deviceID = IDPGuard.tokenParsed?.device_id;
    if (token && typeof deviceID === 'string' && deviceID) {
      try {
        // Revoke this device before removing its bearer token. The IDP's
        // browser logout only clears the cookie session.
        await fetch(
          `${IDPGuard.config.idp_uri}/idp/api/linked-devices/${encodeURIComponent(deviceID)}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
            signal: AbortSignal.timeout(3000),
          },
        );
      } catch {
        // Still clear local credentials if the IDP cannot be reached.
      }
    }
    IDPGuard.authToken = null;
    IDPGuard.tokenParsed = null;
    IDPGuard.removeCookie('ACCESS_TOKEN', '/');
    IDPGuard.removeCookie('REFRESH_TOKEN', '/');
    IDPGuard.removeCookie('TENANT_NAME', '/');
    IDPGuard.removeCookie('ENVIRONMENT', '/');
    try {
      window.sessionStorage.removeItem('art.console.return_to');
    } catch {
      // Best effort.
    }

    IDPGuard.dispatch(
      setToken({ tenantName: null, token: null, clientID: null }),
    );
    IDPGuard.dispatch(setAuthenticated(false));

    const loginUrl = await this.prepareLoginUrl({ stashReturnTo: false });
    window.location.href =
      `${IDPGuard.config.idp_uri}/idp/auth/tenant/logout?redirect_uri=` +
      encodeURIComponent(loginUrl);
  }

  async tokenExchange(): Promise<void> {
    const context = userCode.get(this)!;
    const tenantName = context.tenant || IDPGuard.getCookie('TENANT_NAME');
    if (!tenantName) {
      console.warn('No tenant name available');
      await this.logout();
      return;
    }

    const device = describeBrowser();
    const returnedState = context.returnedState ?? undefined;
    const pkce = consumePkceVerifier(returnedState);
    if (returnedState && !pkce.matched) {
      console.warn('OAuth state mismatch — refusing token exchange');
      await this.logout();
      return;
    }

    const response = await fetch(
      `${IDPGuard.config.idp_uri}/idp/auth/${tenantName}/token-exchange`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: context.code,
          redirect_uri: IDPGuard.config.redirect_uri,
          tenant_name: tenantName,
          code_verifier: pkce.verifier ?? undefined,
          device_id: device.deviceId,
          device_name: device.deviceName,
          platform: device.platform,
          app_version: device.appVersion,
        }),
      },
    );

    if (response.ok) {
      clearLoopBreaker();
      const data = await response.json();
      IDPGuard.setCookie('ACCESS_TOKEN', data.data.access_token);
      IDPGuard.setCookie('REFRESH_TOKEN', data.data.refresh_token);
      IDPGuard.setCookie('TENANT_NAME', tenantName);
      IDPGuard.authToken = data.data.access_token;
      IDPGuard.tokenParsed = IDPGuard.parseToken(data.data.access_token);

      IDPGuard.dispatch(
        setToken({
          tenantName: this.tenantName,
          token: data.data.access_token,
          clientID: this.clientId,
        }),
      );
      IDPGuard.dispatch(setAuthenticated(true));
      userCode.set(this, {
        ...context,
        accessToken: data.data.access_token,
        refreshToken: data.data.refresh_token,
      });

      try {
        const returnTo = window.sessionStorage.getItem('art.console.return_to');
        if (returnTo) {
          window.sessionStorage.removeItem('art.console.return_to');
          window.location.replace(returnTo);
          return;
        }
      } catch {
        // Fall through to the default landing.
      }
      return;
    }

    let detail = `HTTP ${response.status}`;
    try {
      const cloned = response.clone();
      const body = await cloned.json();
      if (body?.message) detail += ` — ${body.message}`;
      if (body?.errors) detail += ` (${JSON.stringify(body.errors)})`;
      console.warn('[auth] Token exchange failed', {
        status: response.status,
        body,
        sentVerifier: !!pkce.verifier,
        sentState: !!returnedState,
      });
    } catch {
      try {
        detail += ` — ${(await response.text()).slice(0, 400)}`;
      } catch {
        // Ignore unreadable response bodies.
      }
      console.warn('[auth] Token exchange failed', {
        status: response.status,
      });
    }

    if (loopBreakerTripped()) {
      console.error(
        '[auth] Two consecutive token exchanges failed — staying signed out. ' +
          'Check the previous warning for the underlying error. Last detail: ' +
          detail,
      );
      clearLoopBreaker();
      IDPGuard.dispatch(setAuthenticated(false));
      return;
    }
    tripLoopBreaker();
    await this.logout();
  }

  async ensureFreshToken(): Promise<void> {
    const expiresAt = IDPGuard.tokenParsed?.exp;
    if (!IDPGuard.authToken || typeof expiresAt !== 'number' || expiresAt <= Date.now() / 1000 + 60) {
      await this.refreshToken();
    }
    if (!IDPGuard.authToken) throw new Error('Session expired');
  }

  refreshToken(): Promise<void> {
    if (!IDPGuard.refreshPromise) {
      IDPGuard.refreshPromise = this.performTokenRefresh().finally(() => {
        IDPGuard.refreshPromise = null;
      });
    }
    return IDPGuard.refreshPromise;
  }

  private async performTokenRefresh(): Promise<void> {
    const context = userCode.get(this);
    const refreshToken =
      IDPGuard.getCookie('REFRESH_TOKEN') || context?.refreshToken;
    if (!refreshToken) {
      console.warn('No refresh token available');
      await this.logout();
      return;
    }

    const tenantName = IDPGuard.getCookie('TENANT_NAME') || context?.tenant;
    if (!tenantName) {
      console.warn('No tenant name available');
      await this.logout();
      return;
    }

    let response: Response;
    try {
      response = await fetch(
        `${IDPGuard.config.idp_uri}/idp/auth/${tenantName}/openid-connect/token/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: IDPGuard.clientId,
            refresh_token: refreshToken,
          }),
          signal: AbortSignal.timeout(15_000),
        },
      );
    } catch {
      throw new TokenRefreshUnavailableError();
    }

    if (response.status === 408 || response.status === 429 || response.status >= 500) {
      throw new TokenRefreshUnavailableError();
    }

    if (response.ok) {
      const data = await response.json();
      IDPGuard.setCookie('ACCESS_TOKEN', data.data.access_token);
      IDPGuard.setCookie('REFRESH_TOKEN', data.data.refresh_token);
      IDPGuard.authToken = data.data.access_token;
      IDPGuard.tokenParsed = IDPGuard.parseToken(data.data.access_token);
      IDPGuard.dispatch(
        setToken({
          tenantName: this.tenantName,
          token: data.data.access_token,
          clientID: this.clientId,
        }),
      );
      IDPGuard.dispatch(setAuthenticated(true));
      userCode.set(this, {
        ...context!,
        accessToken: data.data.access_token,
        refreshToken: data.data.refresh_token,
      });
      this.setupTokenRefreshInterval();
    } else {
      console.warn('Token refreshed with error');
      await this.logout();
    }
  }

  async userData(): Promise<void> {
    if (!IDPGuard.authToken) return;
    try {
      const data = await IDPGuard.config.get_user();
      this.updateUser(data);
    } catch {
      // Keep authentication alive when profile loading fails, matching legacy behavior.
    }
  }

  updateUser(data: any): void {
    IDPGuard.userInfo = data;
    IDPGuard.dispatch(setUser(data));
  }

  get token(): string | null {
    return IDPGuard.authToken;
  }

  get authenticated(): boolean {
    return IDPGuard.authToken != null;
  }

  get clientId(): string | null {
    return IDPGuard.clientId;
  }

  get tenantName(): string | null {
    return IDPGuard.tenantName;
  }

  get tokenParsed(): object | null {
    return IDPGuard.tokenParsed;
  }

  get user(): any | null {
    return IDPGuard.userInfo;
  }
}

function isPkceDisabled(): boolean {
  try {
    if (window.localStorage.getItem('art.console.pkce_disabled') === '1') {
      return true;
    }
  } catch {
    // Ignore unavailable storage.
  }
  return IDPGuardConfigPkceFlag._value === true;
}

const IDPGuardConfigPkceFlag: { _value?: boolean } = {};
const LOOP_BREAKER_KEY = 'art.console.exchange_fail';

function loopBreakerTripped(): boolean {
  try {
    return window.sessionStorage.getItem(LOOP_BREAKER_KEY) === '1';
  } catch {
    return false;
  }
}

function tripLoopBreaker(): void {
  try {
    window.sessionStorage.setItem(LOOP_BREAKER_KEY, '1');
  } catch {
    // Best effort.
  }
}

function clearLoopBreaker(): void {
  try {
    window.sessionStorage.removeItem(LOOP_BREAKER_KEY);
  } catch {
    // Best effort.
  }
}

interface PkcePair {
  verifier: string;
  challenge: string;
}

const PKCE_STORAGE_PREFIX = 'art.console.pkce:';
const PKCE_TTL_MS = 10 * 60 * 1000;

function pkceStorageKey(state: string): string {
  return PKCE_STORAGE_PREFIX + state;
}

async function createPkcePair(): Promise<PkcePair> {
  const verifier = randomBase64Url(32);
  const digest = await sha256(verifier);
  return { verifier, challenge: bufferToBase64Url(digest) };
}

interface PkceLookup {
  verifier?: string;
  expectedFound: boolean;
  matched: boolean;
}

function consumePkceVerifier(state: string | undefined): PkceLookup {
  if (!state) return { expectedFound: false, matched: false };
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(pkceStorageKey(state));
  } catch {
    return { expectedFound: false, matched: false };
  }
  if (!raw) return { expectedFound: false, matched: false };

  try {
    window.sessionStorage.removeItem(pkceStorageKey(state));
  } catch {
    // Best effort.
  }

  try {
    const parsed = JSON.parse(raw) as { verifier: string; createdAt: number };
    if (!parsed.verifier || Date.now() - parsed.createdAt > PKCE_TTL_MS) {
      return { expectedFound: true, matched: false };
    }
    return {
      verifier: parsed.verifier,
      expectedFound: true,
      matched: true,
    };
  } catch {
    return { expectedFound: true, matched: false };
  }
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < byteLength; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return bufferToBase64Url(bytes.buffer);
}

async function sha256(input: string): Promise<ArrayBuffer> {
  const data = new TextEncoder().encode(input);
  return crypto.subtle.digest('SHA-256', data);
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

interface BrowserDescriptor {
  deviceId: string;
  deviceName: string;
  platform: string;
  appVersion: string;
}

const DEVICE_ID_STORAGE_KEY = 'art.console.device_id';

function describeBrowser(): BrowserDescriptor {
  const userAgent =
    (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  const browser = parseBrowser(userAgent);
  const platform = parsePlatform(userAgent);
  const deviceId = persistedDeviceId(userAgent);
  const browserPart =
    browser.name && browser.name !== 'Unknown'
      ? `${browser.name} ${browser.version}`.trim()
      : 'Browser';

  return {
    deviceId,
    deviceName: `ART Console · ${browserPart}`,
    platform,
    appVersion: browserPart,
  };
}

function persistedDeviceId(userAgent: string): string {
  try {
    const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing && existing.length > 8) return existing;
    const fresh = `console:${randomId(16)}`;
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return `console:${simpleHash(userAgent).slice(0, 16)}`;
  }
}

function randomId(bytes: number): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const values = new Uint8Array(bytes);
    crypto.getRandomValues(values);
    return Array.from(values)
      .map((value) => value.toString(16).padStart(2, '0'))
      .join('');
  }
  return Math.random()
    .toString(16)
    .slice(2, 2 + bytes * 2);
}

function simpleHash(input: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function parseBrowser(userAgent: string): { name: string; version: string } {
  const match =
    /(Edge?|OPR|Opera|Chrome|Firefox|Safari)\/?\s?([0-9]+(?:\.[0-9]+)*)/.exec(
      userAgent,
    );
  if (!match) return { name: 'Unknown', version: '' };
  let name = match[1];
  if (name === 'OPR') name = 'Opera';
  if (name === 'Edg') name = 'Edge';
  return { name, version: match[2] };
}

function parsePlatform(userAgent: string): string {
  if (/Mac OS X|Macintosh/.test(userAgent)) return 'macOS';
  if (/Windows/.test(userAgent)) return 'Windows';
  if (/Linux/.test(userAgent)) return 'Linux';
  if (/Android/.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS';
  return 'Web';
}
